import { motion } from "framer-motion";
import { Icon } from "@iconify/react";

const FE_FEATURES = [
    {
        icon: "mdi:lightning-bolt-outline",
        title: "Comunicação em tempo real",
        description:
            "As facturas são enviadas automaticamente para a AGT no momento da emissão, sem qualquer intervenção manual.",
    },
    {
        icon: "mdi:qrcode-scan",
        title: "QR oficial em cada factura",
        description:
            "Cada documento inclui o código QR de verificação da AGT — qualquer pessoa pode confirmar a autenticidade da factura.",
    },
    {
        icon: "mdi:format-list-numbered",
        title: "Séries criadas automaticamente",
        description:
            "As séries de numeração são solicitadas e geridas automaticamente junto da AGT. Zero configuração manual.",
    },
    {
        icon: "mdi:history",
        title: "Envio de facturas anteriores",
        description:
            "Já emitia facturas antes de aderir? Envie o histórico para a AGT com um clique, filtrando por datas.",
    },
];

export default function FacturacaoElectronicaSection() {
    return (
        <section
            id="facturacao-electronica"
            className="relative py-20 lg:py-28 bg-linear-to-br from-blue-950 via-slate-900 to-emerald-950 overflow-hidden"
        >
            {/* Background decorations */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-32 -left-32 w-[400px] h-[400px] rounded-full bg-emerald-500/10 blur-3xl" />
                <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-blue-500/10 blur-3xl" />
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    {/* Left - Content */}
                    <motion.div
                        initial={{ opacity: 0, x: -40 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        {/* NOVO badge */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4 }}
                            className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-400/30 rounded-full px-4 py-1.5 mb-6"
                        >
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
                            </span>
                            <span className="text-sm font-semibold text-emerald-300 tracking-wide uppercase">
                                Novo · Já disponível
                            </span>
                        </motion.div>

                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                            Facturação{" "}
                            <span className="bg-linear-to-r from-emerald-400 to-blue-300 bg-clip-text text-transparent">
                                Electrónica
                            </span>{" "}
                            AGT
                        </h2>

                        <p className="text-lg text-white/60 mb-8 leading-relaxed max-w-xl">
                            O Logigate é certificado pela Administração Geral Tributária para a
                            facturação electrónica em Angola. Emita facturas normalmente — nós
                            comunicamos tudo à AGT por si, em tempo real.
                        </p>

                        {/* Feature list */}
                        <div className="space-y-5 mb-10">
                            {FE_FEATURES.map((feature, index) => (
                                <motion.div
                                    key={feature.title}
                                    initial={{ opacity: 0, x: 20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.3, delay: 0.15 + index * 0.08 }}
                                    className="flex gap-4"
                                >
                                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
                                        <Icon icon={feature.icon} width={20} className="text-emerald-400" />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-semibold text-white mb-0.5">
                                            {feature.title}
                                        </h4>
                                        <p className="text-sm text-white/45 leading-relaxed">
                                            {feature.description}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        <div className="flex flex-wrap gap-4">
                            <a
                                href="/criar"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-colors"
                            >
                                <Icon icon="mdi:rocket-launch-outline" width={20} />
                                Activar facturação electrónica
                            </a>
                            <a
                                href="#precos"
                                onClick={(e) => {
                                    e.preventDefault();
                                    document.querySelector("#precos")?.scrollIntoView({ behavior: "smooth" });
                                }}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/15 text-white font-semibold rounded-xl border border-white/20 transition-colors"
                            >
                                Ver preços
                                <Icon icon="mdi:arrow-right" width={20} />
                            </a>
                        </div>
                    </motion.div>

                    {/* Right - Certificate visual */}
                    <motion.div
                        initial={{ opacity: 0, x: 40 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                    >
                        <div className="relative max-w-md mx-auto">
                            {/* Certificate card */}
                            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 text-center">
                                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-linear-to-br from-emerald-500 to-blue-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                                    <Icon icon="mdi:qrcode-scan" width={40} className="text-white" />
                                </div>

                                <div className="text-sm text-emerald-400 font-medium tracking-wider uppercase mb-2">
                                    Certificação Facturação Electrónica
                                </div>

                                <div className="text-2xl sm:text-3xl font-bold text-white mb-2 font-mono tracking-wider">
                                    FE/220/AGT/2026
                                </div>

                                <p className="text-white/40 text-sm mb-6">
                                    Certificado pela Administração Geral
                                    <br />
                                    Tributária para facturação electrónica
                                </p>

                                <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-2">
                                    <Icon icon="mdi:check-decagram" width={18} className="text-emerald-400" />
                                    <span className="text-sm text-emerald-400 font-medium">
                                        Em produção — a comunicar com a AGT
                                    </span>
                                </div>
                            </div>

                            {/* Floating elements */}
                            <motion.div
                                className="absolute -top-3 -right-3 bg-white rounded-xl shadow-lg px-3 py-2 border border-gray-100"
                                animate={{ y: [0, -6, 0] }}
                                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                            >
                                <div className="flex items-center gap-2">
                                    <Icon icon="mdi:qrcode" width={16} className="text-emerald-600" />
                                    <span className="text-xs font-semibold text-gray-700">
                                        QR de verificação
                                    </span>
                                </div>
                            </motion.div>

                            <motion.div
                                className="absolute -bottom-3 -left-3 bg-white rounded-xl shadow-lg px-3 py-2 border border-gray-100"
                                animate={{ y: [0, 6, 0] }}
                                transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                            >
                                <div className="flex items-center gap-2">
                                    <Icon icon="mdi:lightning-bolt" width={16} className="text-emerald-600" />
                                    <span className="text-xs font-semibold text-gray-700">
                                        Envio em tempo real
                                    </span>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
